import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Calculator, Boxes, FileText, Images, ClipboardList, BadgeCheck, Users, IndianRupee, ShieldCheck, Search, ChevronRight } from "lucide-react";

const CARDS = [
  { to: "/calculator", icon: Calculator, title: "Crate Calculator", sub: "Fit crates in a body", roles: ["admin", "sales", "supervisor", "assistant"], tid: "home-card-calculator" },
  { to: "/catalog", icon: Boxes, title: "Catalog", sub: "Find a container", roles: ["admin", "sales", "supervisor", "assistant"], tid: "home-card-catalog" },
  { to: "/gallery", icon: Images, title: "Gallery", sub: "Photos to share", roles: ["admin", "sales", "supervisor", "assistant"], tid: "home-card-gallery" },
  { to: "/quotes", icon: ClipboardList, title: "My Quotes", sub: "Quotes you created", roles: ["admin", "sales", "assistant"], tid: "home-card-my-quotes" },
  { to: "/pcs", icon: BadgeCheck, title: "My PCs", sub: "Your confirmed orders", roles: ["admin", "sales", "supervisor", "assistant"], tid: "home-card-my-pcs" },
  { to: "/vehicle-management", icon: Search, title: "Vehicle Management", sub: "Search and manage plant vehicles", roles: ["admin", "sales", "supervisor", "assistant", "reefer_team"], tid: "home-card-vehicle-management" },
  { to: "/reefer/quotations", icon: Search, title: "Quotations", sub: "Reefer unit and service quotations", roles: ["admin","sales","supervisor","assistant","reefer_team"], permission: "view_reefer_quotations", anyPermission: ["create_reefer_unit_quotation","create_reefer_service_quotation"], tid: "home-card-reefer-quotations" },
  { to: "/reefer/my-work", icon: Search, title: "My Work", sub: "My quotations, services and installations", roles: ["reefer_team"], tid: "home-card-reefer-my-work" },
  { to: "/reefer/service", icon: Search, title: "Reefer Service", sub: "New service entry and service history", roles: ["admin","sales","supervisor","assistant","reefer_team"], permission: "view_service_entries", anyPermission: ["add_service_entry"], tid: "home-card-reefer-service" },
  { to: "/reefer/installations", icon: Search, title: "Reefer Installation", sub: "Assigned units and installation progress", roles: ["admin","sales","supervisor","assistant","reefer_team"], permission: "view_installation_entries", anyPermission: ["create_installation_entry"], tid: "home-card-reefer-installations" },
  { to: "/admin/reefer-access", icon: Search, title: "Reefer Access", sub: "Permissions and custom model review", roles: ["admin"], tid: "home-card-reefer-access" },
  { to: "/records", icon: Users, title: "All Quotes & PCs", sub: "Everyone's records", roles: ["admin"], tid: "home-card-records" },
  { to: "/prices", icon: IndianRupee, title: "Edit Standard Price", sub: "Update container rates", roles: ["admin"], tid: "home-card-prices" },
  { to: "/admin", icon: ShieldCheck, title: "Admin", sub: "Staff, containers & photos", roles: ["admin"], tid: "home-card-admin" },

  { to: "/checklists", icon: ClipboardList, title: "Checklists", sub: "Create, finish and download order checklists", roles: ["sales", "assistant", "admin", "supervisor"], tid: "home-card-checklists" },];

export default function DashboardPage() {
  const { user } = useAuth();
  const [pa, setPa] = useState(null);

  useEffect(() => { api.get("/pending-actions").then(r => setPa(r.data)).catch(() => {}); }, []);

  const canViewTeamRecords=user.role==="admin" || (["supervisor","sales"].includes(user.role) && (user.permissions?.view_all_records ?? true));
  const cards = CARDS.concat(
    user.role === "assistant"
      ? [{ to: "/assistant/backup", icon: Search, title: "One-Tap Backup",
           sub: "Download operational data", roles: ["assistant"],
           tid: "home-card-assistant-backup" }]
      : []
  ).filter(c => (c.to==="/records" ? canViewTeamRecords : c.roles.includes(user.role)) && (!c.permission || user.role==="admin" || user.permissions?.[c.permission] || (user.role==="assistant" && ["/reefer/quotations","/reefer/service","/reefer/installations"].includes(c.to)) || (c.anyPermission||[]).some(k=>user.permissions?.[k])) && (c.tid!=="home-card-chassis-plant" || ["admin","supervisor"].includes(user.role) || !!user.permissions?.add_chassis));
  const pending = pa ? pa.overdue_quotations.length + pa.unattached_pcs.length + (pa.unattached_chassis?.length || 0) : 0;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900" data-testid="home-greeting">Hello, {user.name.split(" ")[0]}</h1>
      <p className="text-sm text-slate-500 mt-1">What would you like to do today?</p>

      {pa && pending > 0 && (
        <Link
          to="/unresolved"
          data-testid="pending-actions-panel"
          className="mt-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:bg-amber-100/60 transition-colors"
        >
          <div>
            <div className="text-sm font-semibold text-amber-900">{pending} pending action{pending > 1 ? "s" : ""}</div>
            <div className="text-[11px] font-medium text-amber-800 mt-0.5">Tap to choose an unresolved issue</div>
            <div className="text-xs text-amber-700 mt-0.5">
              {pa.overdue_quotations.length > 0 && (
                <span data-testid="overdue-quotations-badge">{pa.overdue_quotations.length} overdue quotation{pa.overdue_quotations.length > 1 ? "s" : ""}</span>
              )}
              {pa.overdue_quotations.length > 0 && pa.unattached_pcs.length > 0 && " · "}
              {pa.unattached_pcs.length > 0 && (<span data-testid="unattached-pcs-badge">{pa.unattached_pcs.length} PC{pa.unattached_pcs.length > 1 ? "s" : ""} awaiting vehicle</span>)}
              {(pa.unattached_chassis?.length || 0) > 0 && <> · <span>{pa.unattached_chassis.length} chassis awaiting PC</span></>}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-amber-700" />
        </Link>
      )}

      <div className="grid grid-cols-2 gap-3 mt-6">
        {cards.map(c => (
          <Link key={c.to} to={c.to} data-testid={c.tid}
            className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-blue-200 transition-all duration-200">
            <div className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center">
              <c.icon className="w-5 h-5 text-white" />
            </div>
            <div className="mt-3.5 text-sm font-semibold text-slate-900">{c.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.sub}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// assistant-dashboard-role-access-20260922

// assistant-backup-dashboard-button-20260923

// reefer-team-frontend-20260928
